"use client";

import { ListFilter } from "lucide-react";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFetchCandidates } from "@/hooks/dao/election/useFetchCandidates";
import { useFetchDaoMembers } from "@/hooks/dao/election/useFetchDaoMembers";
import { formatPersianOrdinal } from "@/lib/persianNumbers";
import { CandidatesEmptyState } from "./CandidatesEmptyState";
import { CandidatesList } from "./CandidatesList";
import { CandidatesSkeleton } from "./CandidatesSkeleton";
import { ElectionRow } from "./ElectionRow";
import { formatVoteCount } from "./VoteCountBadge";

function maxVoteCountDigits(
  rows: { totalReceivedVoteDnmWei: bigint }[],
): number {
  let max = 4;
  for (const r of rows) {
    const len = formatVoteCount(r.totalReceivedVoteDnmWei).length;
    if (len > max) max = len;
  }
  return max;
}

/**
 * Election list section — Figma 5462:51506 (member list) +
 * 5462:51587 (filter icon button) + 5462:51589 (dropdown options).
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────┐
 *   │  [filter icon]                       کاندیدهای سال اول │
 *   │     ↳ click → dropdown opens under the filter icon     │
 *   └────────────────────────────────────────────────────────┘
 *
 * Heading + filter button live on the same row. Heading sits at the start
 * (right under RTL), filter icon button sits at the end (left under RTL).
 *
 * View modes resolved from (kind, cycle):
 *   1. candidates + currentCycle + voting active → CandidatesList (live)
 *   2. candidates + (past cycle OR voting ended)  → ElectionRow rows, no rank,
 *                                                   no vote affordances
 *   3. members + any cycle                        → ElectionRow rows for the
 *                                                   addresses Election.sol has
 *                                                   in ownersOfCycle[cycle].
 *                                                   Contract sorts the slot
 *                                                   array by vote weight, so
 *                                                   slot index = rank — no
 *                                                   client-side sort. Empty
 *                                                   slots (mid-election / not
 *                                                   yet filled) get dropped.
 *
 * Year labels use Persian *word* ordinals — "سال اول", "سال دوم" — not
 * "سال 1" or "سال ۱". The current cycle is shown as "(سال جاری)" instead.
 */

type ListKind = "members" | "candidates";

interface ViewState {
  kind: ListKind;
  cycle: number;
}

interface Option {
  key: string;
  label: string;
  view: ViewState;
}

interface ElectionListSectionProps {
  currentCycle: number;
  cycleEnded: boolean;
}

// Translator for the "dao" namespace, threaded into the pure label builders so
// they stay testable while reading copy from the i18n catalog.
type DaoTranslator = ReturnType<typeof useTranslations<"dao">>;

function yearLabel(
  cycle: number,
  currentCycle: number,
  t: DaoTranslator,
): string {
  return cycle === currentCycle
    ? t("election.candidates.currentYear")
    : t("election.candidates.year", { ordinal: formatPersianOrdinal(cycle) });
}

function headingText(
  view: ViewState,
  currentCycle: number,
  t: DaoTranslator,
): string {
  if (view.kind === "members") {
    return view.cycle === currentCycle
      ? t("election.candidates.membersCurrent")
      : t("election.candidates.members", {
          year: yearLabel(view.cycle, currentCycle, t),
        });
  }
  return t("election.candidates.candidatesOf", {
    year: yearLabel(view.cycle, currentCycle, t),
  });
}

function buildOptions(currentCycle: number, t: DaoTranslator): Option[] {
  const opts: Option[] = [];
  for (let c = currentCycle; c >= 1; c--) {
    const year = yearLabel(c, currentCycle, t);
    opts.push({
      key: `members-${c}`,
      label: t("election.candidates.members", { year }),
      view: { kind: "members", cycle: c },
    });
    opts.push({
      key: `candidates-${c}`,
      label: t("election.candidates.candidatesOf", { year }),
      view: { kind: "candidates", cycle: c },
    });
  }
  return opts;
}

export const ElectionListSection: React.FC<ElectionListSectionProps> = ({
  currentCycle,
  cycleEnded,
}) => {
  const t = useTranslations("dao");
  const defaultView: ViewState = {
    kind: cycleEnded ? "members" : "candidates",
    cycle: currentCycle,
  };
  const [view, setView] = useState<ViewState>(defaultView);
  const options = useMemo(
    () => buildOptions(currentCycle, t),
    [currentCycle, t],
  );

  // Path 1 — live voting view: delegate to the existing CandidatesList so the
  // vote buttons + drawer + invalidation hooks all keep working as before.
  const isLiveVotingView =
    view.kind === "candidates" && view.cycle === currentCycle && !cycleEnded;

  return (
    <div className="flex flex-col gap-3">
      <Header
        heading={headingText(view, currentCycle, t)}
        options={options}
        onSelect={setView}
      />
      {isLiveVotingView ? (
        <CandidatesList />
      ) : (
        <PastList kind={view.kind} cycle={view.cycle} />
      )}
    </div>
  );
};

interface HeaderProps {
  heading: string;
  options: Option[];
  onSelect: (next: ViewState) => void;
}

const Header: React.FC<HeaderProps> = ({ heading, options, onSelect }) => {
  const t = useTranslations("dao");
  return (
    <div className="flex items-center justify-between gap-2">
      {/* First source child renders RIGHT under RTL — heading goes on the
          start side per Figma 5462:51506 + 51587 layout. */}
      <span
        className="text-base font-semibold leading-[1.4] text-text-primary"
        dir="rtl"
      >
        {heading}
      </span>

      {/* Filter button — Figma 5462:51587. Square icon-only with stroke-light
          border + xs radius + tiny shadow. Click opens the dropdown below it. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("election.candidates.listViewAria")}
            className="flex size-8 shrink-0 items-center justify-center rounded-xs border border-stroke-light p-2 text-text-primary shadow-[0px_1px_1px_rgba(0,0,0,0.05)] cursor-pointer"
          >
            <ListFilter className="size-4" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-48">
          {options.map((o) => (
            <DropdownMenuItem
              key={o.key}
              onSelect={() => onSelect(o.view)}
              className="justify-end text-xs text-text-secondary"
              dir="rtl"
            >
              {o.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const PastList: React.FC<{ kind: ListKind; cycle: number }> = ({
  kind,
  cycle,
}) => {
  if (kind === "members") {
    return <MembersList cycle={cycle} />;
  }

  return <CandidateHistoryList cycle={cycle} />;
};

const CandidateHistoryList: React.FC<{ cycle: number }> = ({ cycle }) => {
  const { data, isLoading } = useFetchCandidates(
    cycle === 0 ? undefined : cycle,
  );

  if (isLoading) return <CandidatesSkeleton />;
  if (!data || data.length === 0) return <CandidatesEmptyState />;

  // kind === "candidates" + non-live view: render rows in historical browse
  // mode — no rank tile, no vote affordances, no drawer. interactive=false
  // tells ElectionRow to suppress the vote button + voted badge entirely
  // (vs. rendering them disabled, which is reserved for the live voting view).
  // tileDigits aligns every VoteCountBadge in the list to the widest one.
  const tileDigits = maxVoteCountDigits(data);
  return (
    <div className="flex flex-col gap-2">
      {data.map((c) => (
        <ElectionRow
          key={c.walletAddress}
          candidate={c}
          interactive={false}
          voteTileDigitsCh={tileDigits}
        />
      ))}
    </div>
  );
};

const MembersList: React.FC<{ cycle: number }> = ({ cycle }) => {
  const { data, isLoading } = useFetchDaoMembers(
    cycle === 0 ? undefined : cycle,
  );

  if (isLoading) return <CandidatesSkeleton />;
  if (!data || data.length === 0) return <CandidatesEmptyState />;

  return (
    <div className="flex flex-col gap-2">
      {data.map((c, idx) => (
        <ElectionRow key={c.walletAddress} candidate={c} rank={idx + 1} />
      ))}
    </div>
  );
};
